import FormattedMessage from 'I18n/components/FormattedMessage';
import { getAllChildTeamIdsForParentTeamId, getAllDescendantTeamIdsForParentTeamId, getUserIdsForSelectedTeamIds } from './TeamUtils';

// Utilities for when a user selects/deselects a team, looks at the
// state of selected users and teams and returns an updated selection
// based a teams heirarchy and membership

/**
 * Team selection follows a non-restrictive cascade model.
 *
 * When a team is selected, all of its children are also selected by default.
 *
 * Any or all of the children can be deselected to remove them from the selection. The same is true for the parents of the selected team.
 *
 * Selecting a team _will not_ automatically select the parents of that team.
 *
 * This may change in the future as we refine the underlying logic of nested team relationships.
 */
import { jsx as _jsx } from "react/jsx-runtime";
export const getUpdatedSelectionOfUsersAndTeamsFromTeamAdd = ({
  teams,
  addedTeamId,
  previouslySelectedTeamIds,
  previouslySelectedUserIds,
  cascadeSelection = true
}) => {
  // Get all the child IDs for the team in addition to the newly selected parent team
  const fullListOfTeamIdsToAdd = cascadeSelection ? [addedTeamId, ...getAllDescendantTeamIdsForParentTeamId(addedTeamId, teams)] : [addedTeamId];

  // Dedupe team IDs
  const selectedTeamIds = [...new Set([...previouslySelectedTeamIds, ...fullListOfTeamIdsToAdd])];

  // For all the teams being added, select all the users from all those teams, add them as well.
  const userIdsToAdd = getUserIdsForSelectedTeamIds(fullListOfTeamIdsToAdd, teams);

  // Dedupe user IDs
  const selectedUserIds = [...new Set([...previouslySelectedUserIds, ...userIdsToAdd])];
  return {
    selectedTeamIds,
    selectedUserIds
  };
};
const getAllSelectedChildrenOfParentTeam = ({
  teams,
  parentId,
  selectedTeamIds
}) => {
  return getAllChildTeamIdsForParentTeamId(parentId, teams).filter(teamId => selectedTeamIds.includes(teamId));
};
export const getUpdatedSelectionOfUsersAndTeamsFromTeamRemove = ({
  teams,
  removedTeamId,
  previouslySelectedTeamIds,
  previouslySelectedUserIds
}) => {
  // Create a list of the parent ID and any currently selected child team Ids for removal
  const fullListOfTeamIdsToRemove = [removedTeamId, ...getAllSelectedChildrenOfParentTeam({
    teams,
    parentId: removedTeamId,
    selectedTeamIds: previouslySelectedTeamIds
  })];

  // For any team ID's in this list that where previously selected, those will now be de-selected.
  const newListOfSelectedTeamIds = previouslySelectedTeamIds.filter(id => !fullListOfTeamIdsToRemove.includes(id));

  // For all the teams that are being removed, select all the users from all those teams, remove them as well.
  const userIdsToRemove = getUserIdsForSelectedTeamIds(fullListOfTeamIdsToRemove, teams);

  // From the list of currently selected users, remove any who are members of any of the removed team(s)
  const newListOfSelectedUserIds = previouslySelectedUserIds.filter(id => !userIdsToRemove.includes(id));
  return {
    selectedTeamIds: newListOfSelectedTeamIds,
    selectedUserIds: newListOfSelectedUserIds
  };
};
export const defaultDisallowedTeamFormattedMessage = ({
  teamName
}) => {
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "ui-asset-management-lib.tooltips.disallowedTeamTooltip",
    options: {
      teamName
    }
  });
};